import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/lib/i18n";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  head: () => pageHead({
    path: "/terms",
    title: "Terms of Use — Book Swap",
    description: "The rules for using Book Swap: account eligibility, conduct between neighbors, responsibility for lost or damaged books, and liability limits.",
  }),
  component: Terms,
});

type Section = { en: { title: string; body: string }; ar: { title: string; body: string } };

const sections: Section[] = [
  {
    en: { title: "Acceptance of Terms", body: "By using Book Swap, you agree to abide by these Terms of Use and any applicable local, national, and international laws." },
    ar: { title: "القبول بالشروط", body: "عند استخدامك لموقع BookSwap، فإنك توافق على الالتزام بشروط الاستخدام هذه والقوانين المحلية والوطنية والدولية ذات الصلة." },
  },
  {
    en: { title: "Use of Your Account", body: "You must be 18 years or older to use this site. If you are under 18, please do not use it." },
    ar: { title: "استخدام الحساب الخاص بك", body: "يجب أن يكون عمرك 18 عامًا أو أكبر لاستخدام هذا الموقع. إذا كنت أقل من ذلك، فيرجى عدم استخدامه." },
  },
  {
    en: { title: "Intellectual Property Rights", body: "All content on this site is protected by copyright and other intellectual property laws." },
    ar: { title: "حقوق الملكية الفكرية", body: "جميع المحتويات الموجودة على هذا الموقع محمية بموجب حقوق الطبع والنشر وقوانين الملكية الفكرية الأخرى." },
  },
  {
    en: { title: "User-Submitted Content", body: "You are responsible for any content you post or upload. You grant Book Swap a license to use it within the operation of the service." },
    ar: { title: "استخدام المحتوى المقدم", body: "أنت مسؤول عن أي محتوى تنشره أو ترفعه على الموقع، وتمنح BookSwap ترخيصًا لاستخدامه ضمن نطاق تشغيل الخدمة." },
  },
  {
    en: { title: "User Reviews and Content", body: "Book Swap is not responsible for content created by users or for the reviews they submit." },
    ar: { title: "تقييمات المستخدمين والمحتوى", body: "لا تتحمل BookSwap أي مسؤولية عن أي محتوى ينشئه المستخدمون أو التقييمات التي يقدمها." },
  },
  {
    en: { title: "Removal of Unlawful Content", body: "Book Swap reserves the right to remove any content deemed unlawful or that violates these Terms without prior notice." },
    ar: { title: "إزالة المحتوى غير القانوني", body: "تحتفظ BookSwap بحق إزالة أي محتوى يُعتبر غير قانوني أو ينتهك هذه الشروط دون إشعار مسبق." },
  },
  {
    en: { title: "Book Swap is a platform, not a party to your exchange", body: "Book Swap only helps readers find each other. Every swap, loan, or donation is a private arrangement between the members involved. We do not own, store, inspect, verify, insure, ship, or deliver any book, and we do not act as agent, broker, or escrow for any member." },
    ar: { title: "Book Swap منصة وليست طرفًا في التبادل", body: "تساعد Book Swap القرّاء على التواصل فقط. كل تبادل أو إعارة أو تبرع هو اتفاق خاص بين الأعضاء المعنيين. نحن لا نملك أي كتاب ولا نخزّنه ولا نفحصه ولا نتحقق منه ولا نؤمّن عليه ولا نشحنه، ولا نعمل كوكيل أو وسيط أو أمين ضمان لأي عضو." },
  },
  {
    en: { title: "Damaged, lost, stolen or unreturned books", body: "You accept the full risk of handing your book to another member. Book Swap is not responsible and offers no refund, replacement, compensation, or guarantee if a book is damaged, lost, stolen, kept, never returned, arrives in a different condition than described, or is never handed over at all. Agree on condition, return dates, and any deposit directly with the other member before you meet." },
    ar: { title: "الكتب التالفة أو المفقودة أو المسروقة أو غير المعادة", body: "أنت تتحمل كامل المخاطر عند تسليم كتابك لعضو آخر. لا تتحمل Book Swap أي مسؤولية ولا تقدم أي استرداد أو بديل أو تعويض أو ضمان إذا تعرض الكتاب للتلف أو الفقد أو السرقة أو الاحتفاظ به أو عدم إعادته أو وصوله بحالة مختلفة عن الوصف أو عدم تسليمه أصلًا. اتفق على الحالة وموعد الإرجاع وأي مبلغ تأمين مباشرة مع العضو الآخر قبل اللقاء." },
  },
  {
    en: { title: "Meeting other members safely", body: "You are solely responsible for your own safety. Meet in public places, do not share your exact home address unless you choose to, and follow local laws. Book Swap does not perform background checks on members and is not liable for the conduct of any member online or in person." },
    ar: { title: "اللقاء مع الأعضاء بأمان", body: "أنت وحدك المسؤول عن سلامتك. التقِ في أماكن عامة، ولا تشارك عنوان منزلك الدقيق إلا باختيارك، والتزم بالأنظمة المحلية. لا تجري Book Swap فحصًا للسجلات الشخصية للأعضاء ولا تتحمل مسؤولية سلوك أي عضو عبر الإنترنت أو على أرض الواقع." },
  },
  {
    en: { title: "Prohibited listings and conduct", body: "Do not list pirated, counterfeit, stolen, or illegal material, do not list items other than books and reading material, and do not harass, threaten, spam, scrape, or impersonate anyone. We may suspend or remove any account that breaks these rules, at our discretion and without notice." },
    ar: { title: "المحتوى والسلوك الممنوع", body: "يُمنع عرض المواد المقرصنة أو المقلدة أو المسروقة أو غير القانونية، ويُمنع عرض أي مواد غير الكتب والمواد القرائية، ويُمنع التحرش أو التهديد أو الإزعاج أو السحب الآلي للبيانات أو انتحال الشخصية. يحق لنا تعليق أو إزالة أي حساب يخالف هذه القواعد وفق تقديرنا ودون إشعار." },
  },
  {
    en: { title: "Service provided \"as is\"", body: "The service is provided as is and as available, without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, accuracy of listings or distances, or uninterrupted availability. Features may change or stop at any time." },
    ar: { title: "الخدمة مقدمة \"كما هي\"", body: "تُقدَّم الخدمة كما هي وحسب توفرها، دون أي ضمانات صريحة أو ضمنية، بما في ذلك القابلية للتسويق أو الملاءمة لغرض معين أو دقة العروض أو المسافات أو استمرارية التوفر. قد تتغير المزايا أو تتوقف في أي وقت." },
  },
  {
    en: { title: "Limitation of liability", body: "To the fullest extent permitted by law, Book Swap and its owner are not liable for any indirect, incidental, or consequential loss, or for any loss of books, property, data, money, or reputation arising from your use of the service or from your dealings with other members. Where liability cannot be excluded, it is limited to the amount you paid us, which for a free account is zero." },
    ar: { title: "حدود المسؤولية", body: "إلى أقصى حد يسمح به النظام، لا تتحمل Book Swap ولا مالكها أي مسؤولية عن أي خسارة غير مباشرة أو عرضية أو تبعية، ولا عن فقدان الكتب أو الممتلكات أو البيانات أو الأموال أو السمعة الناتجة عن استخدامك للخدمة أو تعاملك مع الأعضاء الآخرين. وحيثما لا يمكن استبعاد المسؤولية، فإنها تقتصر على المبلغ الذي دفعته لنا، وهو صفر في الحسابات المجانية." },
  },
  {
    en: { title: "Indemnity", body: "You agree to cover Book Swap and its owner against any claim, loss, or legal cost that results from your listings, your messages, your exchanges, or your breach of these Terms." },
    ar: { title: "التعويض", body: "توافق على تعويض Book Swap ومالكها عن أي مطالبة أو خسارة أو تكلفة قانونية تنتج عن عروضك أو رسائلك أو تبادلاتك أو مخالفتك لهذه الشروط." },
  },
  {
    en: { title: "Disputes between members", body: "If a disagreement arises over a book, please try to resolve it directly. We may, but are not obliged to, review reports and take account action. We do not arbitrate disputes, recover books, or award compensation." },
    ar: { title: "النزاعات بين الأعضاء", body: "في حال نشوء خلاف حول كتاب، يُرجى محاولة حله مباشرة. قد نراجع البلاغات ونتخذ إجراءً بشأن الحسابات، دون التزام بذلك. نحن لا نفصل في النزاعات ولا نسترد الكتب ولا نمنح تعويضات." },
  },
  {
    en: { title: "Account suspension and termination", body: "You may delete your account at any time. We may suspend or terminate accounts that breach these Terms, abuse the service, or put other members at risk." },
    ar: { title: "تعليق الحساب وإنهاؤه", body: "يمكنك حذف حسابك في أي وقت. ويحق لنا تعليق أو إنهاء الحسابات التي تخالف هذه الشروط أو تسيء استخدام الخدمة أو تعرّض الأعضاء الآخرين للخطر." },
  },
  {
    en: { title: "Modification of Terms", body: "Book Swap may modify these Terms at any time. Continued use of the site constitutes acceptance of the changes." },
    ar: { title: "تعديل الشروط والأحكام", body: "يحق لـ BookSwap تعديل هذه الشروط في أي وقت، ويعتبر استمرار استخدامك للموقع موافقةً على التعديلات." },
  },
  {
    en: { title: "Governing Law", body: "These Terms are governed by the laws applicable in the jurisdiction where Book Swap operates." },
    ar: { title: "القانون الواجب التطبيق", body: "تخضع هذه الشروط للقوانين المعمول بها في الولاية القضائية التي تعمل فيها BookSwap." },
  },
  {
    en: { title: "Privacy Policy", body: "Your data collection and use are subject to our Privacy Policy, which forms part of these Terms." },
    ar: { title: "سياسة الخصوصية", body: "يخضع جمع بياناتك واستخدامها لسياسة الخصوصية الخاصة بنا، والتي تُعتبر جزءًا من هذه الشروط." },
  },
  {
    en: { title: "Contact Us", body: "For any questions about these Terms, please email z3phyr11@outlook.com or visit our {{contact}}." },
    ar: { title: "التواصل معنا", body: "لأي استفسار حول هذه الشروط، راسلنا على z3phyr11@outlook.com أو زر {{contact}}." },
  },
  {
    en: { title: "Updates to These Terms", body: "We will post any updates to these Terms on this page along with the date of the latest revision." },
    ar: { title: "تحديثات الشروط", body: "سننشر أي تحديثات لهذه الشروط على هذه الصفحة مع الإشارة إلى تاريخ آخر تحديث." },
  },
];

function Terms() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10" dir={isAr ? "rtl" : "ltr"}>
        <div className="rounded-3xl p-8 text-white shadow-[var(--shadow-elegant)]" style={{ background: "var(--gradient-hero)" }}>
          <p className="text-sm text-white/80">Book Swap</p>
          <h1 className="mt-1 font-serif text-3xl text-white md:text-4xl">
            {isAr ? "شروط و استخدامات الموقع" : "Terms of Use"}
          </h1>
          <p className="mt-2 text-white/80">
            {isAr ? "النسخة 1.0 — المالك: BookSwap" : "Version 1.0 — Owner: Book Swap"}
          </p>
        </div>

        <div className="mt-8 space-y-6 rounded-2xl bg-card p-6 shadow-[var(--shadow-card)] leading-loose text-foreground">
          {sections.map((s, i) => {
            const node = isAr ? s.ar : s.en;
            const parts = node.body.split("{{contact}}");
            return (
              <section key={i}>
                <h2 className="font-serif text-lg font-semibold text-primary-dark">
                  {i + 1}. {node.title}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {parts[0]}
                  {parts.length > 1 && (
                    <>
                      <Link to="/contact" className="font-medium text-primary hover:underline">
                        {isAr ? "صفحة التواصل" : "Contact page"}
                      </Link>
                      {parts[1]}
                    </>
                  )}
                </p>
              </section>
            );
          })}
          <p className="pt-4 text-xs text-muted-foreground">
            {isAr ? "شروط الاستخدام · النسخة 1.0 · آخر تحديث مايو 2026" : "Terms of Use · Version 1.0 · Last updated May 2026"}
          </p>
        </div>
      </div>
    </div>
  );
}
